import React, { useState, useEffect } from 'react';
import {
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Input,
    HStack,
    IconButton,
    useToast,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Text,
    Button,
    InputGroup,
    InputLeftElement,
    Badge,
    Avatar,
    SimpleGrid,
    VStack,
    Flex,
    Divider,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, SearchIcon } from '@chakra-ui/icons';
import FloatingAddButton from '../components/FloatingAddButton';
import PropietarioDetails from './Propietarios/PropietarioView';
import TableSkeleton from '../components/TableSkeleton';

const PropietarioTesoreria: React.FC = () => {
    const [transactions, setTransactions] = useState([]);
    const [selectedPredioId, setSelectedPredioId] = useState<string | null>(null);
    const [isModalOpen, setModalOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(15);
    const [totalPages, setTotalPages] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);

    const toast = useToast();

    const fetchTransactions = async () => {
        try {
            setLoading(true);
            const response = await fetch(
                `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/propietarios?page=${page}&pageSize=${pageSize}&search=${searchQuery}`
            );
            const data = await response.json();
            setTransactions(data.data);
            setTotalPages(data.pagination.totalPages);
        } catch (error) {
            toast({
                title: 'Error',
                description: 'No se pudieron cargar los datos.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTransactions();
    }, [page, pageSize, searchQuery]);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        setPage(1);
    };

    const handleRowClick = (id: string) => {
        setSelectedPredioId(id);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setSelectedPredioId(null);
    };

    const handleOpenModal = () => {
        setSelectedPredioId(null);
        setModalOpen(true);
    }

    return (
        <Box p={4} pt={79} width="100%">
            {/* Barra de búsqueda */}
            <Box mb={6}>
                <InputGroup maxW="400px">
                    <InputLeftElement pointerEvents="none">
                        <SearchIcon color="gray.300" />
                    </InputLeftElement>
                    <Input 
                        placeholder="Buscar propietarios..." 
                        value={searchQuery} 
                        onChange={handleSearch}
                        bg="white"
                        borderColor="gray.200"
                        _focus={{ borderColor: "#bb8c52", boxShadow: "0 0 0 1px #bb8c52" }}
                    />
                </InputGroup>
            </Box>

            {/* Tarjetas de Propietarios */}
            {loading ? (
                <TableSkeleton rows={pageSize} columns={5} hasAvatar={true} />
            ) : (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={5} mb={6}>
                    {transactions.map((propietario: any) => (
                        <Box
                            key={propietario.id}
                            bg="white"
                            borderRadius="xl"
                            boxShadow="md"
                            border="2px solid"
                            borderColor="gray.200"
                            p={5}
                            cursor="pointer"
                            transition="all 0.3s"
                            onClick={() => handleRowClick(propietario.id)}
                            _hover={{
                                transform: 'translateY(-6px)',
                                boxShadow: '2xl',
                                borderColor: '#bb8c52',
                            }}
                        >
                            <VStack spacing={4} align="stretch">
                                {/* Header con Avatar */}
                                <Flex justify="space-between" align="start">
                                    <HStack spacing={3}>
                                        <Avatar
                                            size="md"
                                            name={`${propietario.nombre} ${propietario.primerApellido}`}
                                            bg="linear-gradient(135deg, #681a2d 0%, #5b182b 100%)"
                                            color="white"
                                        />
                                        <Badge
                                            bg="#bb8c52"
                                            color="white"
                                            fontSize="xs"
                                            borderRadius="full"
                                            px={3}
                                            py={1}
                                        >
                                            ID #{propietario.id}
                                        </Badge>
                                    </HStack>
                                </Flex>

                                <Divider borderColor="gray.300" />

                                {/* Nombre Completo */}
                                <Box>
                                    <Text fontSize="xs" color="gray.500" fontWeight="semibold" mb={1}>
                                        👤 Nombre Completo
                                    </Text>
                                    <Text
                                        fontSize="md"
                                        fontWeight="bold"
                                        color="#681a2d"
                                        noOfLines={2}
                                    >
                                        {propietario.nombre} {propietario.primerApellido}
                                    </Text>
                                    {propietario.segundoApellido && (
                                        <Text fontSize="sm" color="gray.600" fontWeight="medium">
                                            {propietario.segundoApellido}
                                        </Text>
                                    )}
                                </Box>

                                {/* Razón Social (si existe) */}
                                {propietario.razonSocial && (
                                    <Box>
                                        <Text fontSize="xs" color="gray.500" fontWeight="semibold" mb={1}>
                                            🏢 Razón Social
                                        </Text>
                                        <Text
                                            fontSize="sm"
                                            color="#681a2d"
                                            bg="#f0e6d2"
                                            px={3}
                                            py={2}
                                            borderRadius="md"
                                            noOfLines={2}
                                        >
                                            {propietario.razonSocial}
                                        </Text>
                                    </Box>
                                )}

                                {/* Tipo de Contribuyente */}
                                <Flex justify="space-between" align="center">
                                    <Text fontSize="xs" color="gray.500" fontWeight="semibold">
                                        📋 Tipo
                                    </Text>
                                    <Badge
                                        colorScheme={propietario.idTipoContribuyente === 'F' ? 'purple' : 'blue'}
                                        fontSize="xs"
                                        px={2}
                                        py={1}
                                        borderRadius="md"
                                    >
                                        {propietario.idTipoContribuyente === 'F' ? 'Físico' : 'Moral'}
                                    </Badge>
                                </Flex>

                                {/* Estado Titular */}
                                {propietario.esTitular && (
                                    <Badge
                                        bg="linear-gradient(135deg, #681a2d 0%, #5b182b 100%)"
                                        color="white"
                                        fontSize="xs"
                                        px={3}
                                        py={1}
                                        borderRadius="md"
                                        textAlign="center"
                                    >
                                        ⭐ Titular
                                    </Badge>
                                )}
                            </VStack>
                        </Box>
                    ))}
                </SimpleGrid>
            )}

            {/* Paginación */}
            <HStack spacing={4} justify="center">
                <IconButton
                    icon={<ChevronLeftIcon />}
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    isDisabled={page === 1}
                    aria-label="Página anterior"
                    variant="outline"
                    borderColor="#bb8c52"
                    color="#bb8c52"
                    _hover={{ bg: "#bb8c52", color: "white" }}
                    size="sm"
                />
                <Text color="#681a2d" fontWeight="medium" fontSize="sm">
                    Página {page} de {totalPages}
                </Text>
                <IconButton
                    icon={<ChevronRightIcon />}
                    onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                    isDisabled={page === totalPages}
                    aria-label="Página siguiente"
                    variant="outline"
                    borderColor="#bb8c52"
                    color="#bb8c52"
                    _hover={{ bg: "#bb8c52", color: "white" }}
                    size="sm"
                />
            </HStack>
            
            <FloatingAddButton 
                text="Nuevo Propietario" 
                onClick={handleOpenModal} 
            />

            {/* Modal */}
            <Modal isOpen={isModalOpen} onClose={closeModal} size="4xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader
                        bgImage="url('/img/bg.webp')"
                        bgSize="cover"
                        bgPosition="center"
                        bgRepeat="no-repeat"
                        color="white"
                        textShadow="2px 2px 4px rgba(0,0,0,0.7)"
                        fontWeight="bold"
                        position="relative"
                    >
                        {selectedPredioId ? 'Detalles del Propietario' : 'Crear Propietario'}
                    </ModalHeader>
                    <ModalCloseButton color="white" _hover={{ bg: 'rgba(255,255,255,0.2)' }} />
                    <ModalBody>
                        <PropietarioDetails propietarioId={selectedPredioId} />
                    </ModalBody>
                    <ModalFooter>
                        <Button bg="#5b182b" color="white" _hover={{ bg: "#691f30" }} onClick={closeModal}>
                            Cerrar
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default PropietarioTesoreria;
